import React from "react";
import { List } from "immutable";
import FileSaver from "file-saver";
import initial from "lodash/initial";
import isEqual from "lodash/isEqual";
import castArray from "lodash/castArray";
import moment, { isMoment } from "moment";
import isFunction from "lodash/isFunction";
import { Grid, Typography } from "@material-ui/core";
import scrollIntoView from "scroll-into-view-if-needed";
import { defaultMemoize, createSelectorCreator } from "reselect";

import { formatDate } from "utils/format.util";
import { EMPTY_LIST, APP_ROLES } from "app/app.constants";

export const createSelector = createSelectorCreator(defaultMemoize, isEqual);

export const invokeIfFunction = (fn, ...args) => isFunction(fn) && fn(...args);

export const getInitials = (displayName = "") => {
  return displayName
    .split(/\s/)
    .map(s => s.charAt(0).toUpperCase())
    .join("");
};

export const getFileBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

export const makeActionCreator = (action, ...argNames) => {
  const { payload: payloadFn } = action;

  return (...args) => dispatch => {
    const payloadResult = payloadFn(...args);

    const payload = isFunction(payloadResult)
      ? dispatch(payloadResult)
      : payloadResult;

    const updatedAction = argNames.reduce(
      (sum, curr, index) => ({
        ...sum,
        [argNames[index]]: args[index],
      }),
      {
        ...action,
        payload,
      },
    );

    dispatch(updatedAction);

    return payload;
  };
};

export const getFileName = fullFileName => {
  const parts = fullFileName.split(".");

  return initial(parts).join(".");
};

export const renderContent = (content, props) =>
  isFunction(content) ? content(props) : content;

export const generateFormName = (...parts) => parts.join(".");

export const getAvatar = avatar =>
  avatar ? `data:image/png;base64,${avatar}` : undefined;

export const createGuid = () => {
  const S4 = () =>
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);

  return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
};

export const getNumberOfMonths = (startDate, endDate) =>
  [startDate, endDate].every(date => isMoment(date))
    ? Math.ceil(
        endDate
          .clone()
          .endOf("month")
          .diff(startDate.clone().startOf("month"), "months", true),
      )
    : 0;

export const getNumberOfWeeks = (startDate, endDate) =>
  [startDate, endDate].every(date => isMoment(date))
    ? Math.ceil(
        endDate
          .clone()
          .endOf("isoWeek")
          .diff(startDate.clone().startOf("isoWeek"), "weeks", true),
      )
    : 0;

export const getNumberOfDays = (startDate, endDate) =>
  [startDate, endDate].every(date => isMoment(date))
    ? Math.ceil(endDate.diff(startDate, "days", true))
    : 0;

export const getModifiedByValue = item => (
  <Grid>
    <Typography noWrap variant="body2">
      {item.get("modifiedBy")}
    </Typography>
    <Typography noWrap variant="caption" component="p">
      {formatDate(item.get("modified"))}
    </Typography>
  </Grid>
);

export const scrollSmoothlyIntoViewIfNeeded = element =>
  element &&
  scrollIntoView(element, {
    behavior: "smooth",
    scrollMode: "if-needed",
  });

export const isDateWithinDateRange = (date, dateRange, inclusivity) =>
  date
    .clone()
    .startOf("day")
    .isBetween(
      dateRange
        .get("startDate")
        .clone()
        .startOf("day"),
      dateRange
        .get("endDate")
        .clone()
        .endOf("day"),
      "day",
      inclusivity,
    );

export const dateIsWithinStartAndEndDate = (date, startDate, endDate) =>
  date.isBetween(startDate, endDate, "day", "[]");

export const isSelected = (date, selectedStartDate, selectedEndDate) =>
  selectedStartDate && selectedEndDate
    ? dateIsWithinStartAndEndDate(date, selectedStartDate, selectedEndDate)
    : false;

export const saveFile = (blob, name) => FileSaver.saveAs(blob, name);

export const dateFromDateHourMinute = (date, hour, minute) =>
  moment.isMoment(date)
    ? date
        .set("second", 0)
        .set("hour", hour)
        .set("minute", minute)
        .set("millisecond", 0)
    : undefined;

export const groupByOverlappingDateRanges = dateRanges => {
  const dateRangesSorted = dateRanges.sortBy(dr => dr.get("startDate"));

  return dateRangesSorted.reduce((dateRangesGroups, dateRange) => {
    if (!dateRangesGroups.size)
      return dateRangesGroups.set(0, List.of(dateRange));

    const lastOverlappingDateRange = dateRangesGroups
      .get(dateRangesGroups.size - 1)
      .findLast(dr =>
        dr.get("endDate").isSameOrAfter(dateRange.get("startDate"), "day"),
      );

    return lastOverlappingDateRange
      ? dateRangesGroups.update(
          dateRangesGroups.size - 1,
          overlappingDateRanges =>
            overlappingDateRanges.push(
              dateRange.set(
                "groupIndex",
                lastOverlappingDateRange.get("groupIndex", 0) + 1,
              ),
            ),
        )
      : dateRangesGroups.set(dateRangesGroups.size, EMPTY_LIST.push(dateRange));
  }, EMPTY_LIST);
};

export const getCalendarDateRangeLeftOffset = (
  minDate,
  totalDays,
  dateRange,
) => {
  const days = Math.max(dateRange.get("startDate").diff(minDate, "days"), 0);

  return `${(days / totalDays) * 100}%`;
};

export const getCalendarDateRangeRightOffset = (
  maxDate,
  totalDays,
  dateRange,
) => {
  const days = Math.max(maxDate.diff(dateRange.get("endDate"), "days"), 0);

  return `${(days / totalDays) * 100}%`;
};

export const getCalendarDateRangeHeight = overlapDateRangeCount => {
  return `calc(${100 / overlapDateRangeCount}% - 1px)`;
};

export const getCalendarDateRangeTopOffset = overlapDateRangeIndex => {
  return `translateY(calc(${overlapDateRangeIndex *
    100}% + ${overlapDateRangeIndex}px))`;
};

export const hasRequiredAccessLevel = (accessLevel, appRoles) => {
  if (!accessLevel) {
    return true;
  }

  return appRoles
    ? castArray(accessLevel).some(role => appRoles.includes(role))
    : false;
};

const handleValidRoute = currentUser => route => {
  if (!route.routes) return route;

  return {
    ...route,
    routes: filterValidRoutesForUser(currentUser)(route.routes),
  };
};

const filterValidSubRoutes = currentUser => (validRoutes, route) => {
  const userHasRequiredAccessLevel = hasRequiredAccessLevel(
    route.accessLevel,
    currentUser.get("appRoles"),
  );

  return userHasRequiredAccessLevel
    ? [...validRoutes, handleValidRoute(currentUser)(route)]
    : validRoutes;
};

export const filterValidRoutesForUser = currentUser => routes =>
  routes.reduce(filterValidSubRoutes(currentUser), []);

export const isViewer = appRoles =>
  appRoles ? appRoles.includes(APP_ROLES.VIEWER) : false;

export const isAdmin = appRoles =>
  appRoles ? appRoles.includes(APP_ROLES.ADMIN) : false;

export const isPlanner = appRoles =>
  appRoles ? appRoles.includes(APP_ROLES.PLANNER) : false;
